'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../libs/utils';
import { Divider } from '../divider/Divider';

type AccordionRootProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>;
type AccordionMultipleProps = Extract<AccordionRootProps, { type: 'multiple' }>;
type AccordionSingleProps = Extract<AccordionRootProps, { type: 'single' }>;

type AccordionCommonProps = {
  divided?: boolean;
};

type AccordionMultipleModeProps = { isMultiple?: true } & Omit<AccordionMultipleProps, 'type' | 'disabled'>;
type AccordionSingleModeProps = { isMultiple: false } & Omit<AccordionSingleProps, 'type' | 'collapsible' | 'disabled'>;

export type AccordionProps = AccordionCommonProps &
  (AccordionMultipleModeProps | AccordionSingleModeProps) & {
    ref?: React.Ref<React.ComponentRef<typeof AccordionPrimitive.Root>>;
  };

const withDividers = (children: React.ReactNode) =>
  React.Children.toArray(children).map((child, index) => (
    <React.Fragment key={React.isValidElement(child) ? (child.key ?? index) : index}>
      {index > 0 && <Divider aria-hidden="true" className="!bg-neutral-border-high" />}
      {child}
    </React.Fragment>
  ));

const Accordion = ({ className, isMultiple = true, divided = false, children, ref, ...props }: AccordionProps) => {
  const content = divided ? withDividers(children) : children;

  if (isMultiple) {
    return (
      <AccordionPrimitive.Root ref={ref} type="multiple" className={className} {...(props as Omit<AccordionMultipleProps, 'type' | 'disabled'>)}>
        {content}
      </AccordionPrimitive.Root>
    );
  }

  return (
    <AccordionPrimitive.Root
      ref={ref}
      type={'single'}
      collapsible
      className={className}
      {...(props as Omit<AccordionSingleProps, 'type' | 'collapsible' | 'disabled'>)}
    >
      {content}
    </AccordionPrimitive.Root>
  );
};
Accordion.displayName = 'Accordion';

type AccordionBg = 'none' | 'body' | 'full';

const AccordionItemContext = React.createContext<{ bg: AccordionBg }>({ bg: 'none' });

const accordionItemVariants = cva('', {
  variants: {
    bg: {
      none: '',
      body: '',
      full: 'rounded-bzc-medium bg-neutral-background-lowest px-normal-medium py-tiny-medium'
    }
  },
  defaultVariants: {
    bg: 'none'
  }
});

export type AccordionItemProps = React.ComponentPropsWithRef<typeof AccordionPrimitive.Item> & VariantProps<typeof accordionItemVariants>;

const AccordionItem = ({ className, bg = 'none', ref, ...props }: AccordionItemProps) => (
  <AccordionItemContext.Provider value={{ bg: bg ?? 'none' }}>
    <AccordionPrimitive.Item ref={ref} className={cn(accordionItemVariants({ bg }), className)} {...props} />
  </AccordionItemContext.Provider>
);
AccordionItem.displayName = 'AccordionItem';

export type AccordionChevronProps = Omit<React.ComponentPropsWithRef<'svg'>, 'width' | 'height'> & {
  size?: number;
  color?: string;
};

const AccordionChevron = ({ className, style, size = 18, color, ref, ...props }: AccordionChevronProps) => (
  <svg
    ref={ref}
    viewBox="0 0 20 20"
    width={size}
    height={size}
    fill="none"
    aria-hidden="true"
    style={{ transitionDuration: '400ms', color, ...style }}
    className={cn(
      'shrink-0 translate-y-[3px] text-neutral-foreground-lowest transition-transform ease-[ease] group-data-[state=open]/trigger:-translate-y-[3px]',
      className
    )}
    {...props}
  >
    <line
      x1="2.5"
      y1="10"
      x2="10"
      y2="10"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      style={{ transitionDuration: '400ms' }}
      className={cn(
        'transition-transform ease-[ease] [transform-box:view-box] [transform-origin:10px_10px]',
        'rotate-45 group-data-[state=open]/trigger:-rotate-45'
      )}
    />
    <line
      x1="17.5"
      y1="10"
      x2="10"
      y2="10"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      style={{ transitionDuration: '400ms' }}
      className={cn(
        'transition-transform ease-[ease] [transform-box:view-box] [transform-origin:10px_10px]',
        '-rotate-45 group-data-[state=open]/trigger:rotate-45'
      )}
    />
  </svg>
);
AccordionChevron.displayName = 'AccordionChevron';

const AccordionTrigger = ({ className, children, ref, ...props }: React.ComponentPropsWithRef<typeof AccordionPrimitive.Trigger>) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'group/trigger body-small-semibold flex w-full cursor-pointer items-center justify-between gap-tiny-large text-left text-neutral-foreground-main outline-none',
        className
      )}
      {...props}
    >
      {children}
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = ({ className, children, ref, ...props }: React.ComponentPropsWithRef<typeof AccordionPrimitive.Content>) => {
  const { bg } = React.useContext(AccordionItemContext);

  return (
    <AccordionPrimitive.Content
      ref={ref}
      className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div
        className={cn(
          'pb-normal-medium',
          bg !== 'body' && 'pt-tiny-medium',
          'caption-medium-regular text-neutral-foreground-low',
          bg === 'body' && 'rounded-bzc-medium bg-neutral-background-lowest p-normal-medium mb-normal-medium',
          className
        )}
      >
        {children}
      </div>
    </AccordionPrimitive.Content>
  );
};
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent, AccordionChevron };
